import { Link } from "@inertiajs/react"

import { employeesPath } from "@/routes"

type PermissionCategories = Record<string, string[]>

interface EmployeeFormData {
  first_name: string
  last_name: string
  initials: string
  formal_name: string
  report_display_name: string
  job_title: string
  email: string
  username: string
  work_phone: string
  cell_phone: string
  home_phone: string
  status: string
  employment_type: number | null
  worker_type: number
  org_level: number | null
  pay_rate: number | null
  pay_type: number
  mileage_rate: number | null
  pto_days: number | null
  rdo_days: number | null
  sick_days: number | null
  specialty: string
  quickbooks_name: string
  quickbooks_vendor: string
  sig_profile: string
  sig_vcard: string
  sig_show_cell: boolean
  permissions: Record<string, boolean>
  roles: string[]
  password?: string
  password_confirmation?: string
}

interface Props {
  data: EmployeeFormData
  setData: (key: string, value: unknown) => void
  errors?: Record<string, string[]>
  processing: boolean
  onSubmit: (e: React.FormEvent) => void
  submitLabel: string
  permission_categories: PermissionCategories
  role_options: string[]
  isNew?: boolean
  isAdmin?: boolean
}

export default function EmployeeForm({
  data,
  setData,
  errors,
  processing,
  onSubmit,
  submitLabel,
  permission_categories,
  role_options,
  isNew = false,
  isAdmin = false,
}: Props) {
  const togglePermission = (perm: string) => {
    const updated = { ...data.permissions, [perm]: !data.permissions[perm] }
    setData("permissions", updated)
  }

  const toggleRole = (role: string) => {
    const roles = data.roles.includes(role)
      ? data.roles.filter((r) => r !== role)
      : [...data.roles, role]
    setData("roles", roles)
  }

  return (
    <form onSubmit={onSubmit} className="max-w-6xl">
      <div className="mb-6 grid gap-6 md:grid-cols-2">
        {/* Basic Info */}
        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Basic Information</h2>

          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label htmlFor="first_name" className="mb-1 block font-medium">
                First Name
              </label>
              <input
                type="text"
                id="first_name"
                value={data.first_name}
                onChange={(e) => setData("first_name", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                required
              />
              {errors?.first_name && (
                <p className="text-hpi-red mt-1 text-sm">
                  {errors.first_name.join(", ")}
                </p>
              )}
            </div>
            <div>
              <label htmlFor="last_name" className="mb-1 block font-medium">
                Last Name
              </label>
              <input
                type="text"
                id="last_name"
                value={data.last_name}
                onChange={(e) => setData("last_name", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                required
              />
            </div>
          </div>

          <div className="mb-4 grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="initials" className="mb-1 block font-medium">
                Initials
              </label>
              <input
                type="text"
                id="initials"
                value={data.initials}
                onChange={(e) => setData("initials", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                maxLength={5}
              />
            </div>
            <div className="md:col-span-2">
              <label htmlFor="formal_name" className="mb-1 block font-medium">
                Formal Name
              </label>
              <input
                type="text"
                id="formal_name"
                value={data.formal_name}
                onChange={(e) => setData("formal_name", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
          </div>

          <div className="mb-4">
            <label htmlFor="job_title" className="mb-1 block font-medium">
              Job Title
            </label>
            <input
              type="text"
              id="job_title"
              value={data.job_title}
              onChange={(e) => setData("job_title", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            />
          </div>

          <div className="mb-4">
            <label htmlFor="email" className="mb-1 block font-medium">
              Email
            </label>
            <input
              type="email"
              id="email"
              value={data.email}
              onChange={(e) => setData("email", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              required
            />
            {errors?.email && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.email.join(", ")}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="username" className="mb-1 block font-medium">
              Username
            </label>
            <input
              type="text"
              id="username"
              value={data.username}
              onChange={(e) => setData("username", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            />
            {errors?.username && (
              <p className="text-hpi-red mt-1 text-sm">
                {errors.username.join(", ")}
              </p>
            )}
          </div>

          <div className="mb-4">
            <label htmlFor="status" className="mb-1 block font-medium">
              Status
            </label>
            <select
              id="status"
              value={data.status}
              onChange={(e) => setData("status", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            >
              <option value="active">Active</option>
              <option value="inactive">Inactive</option>
            </select>
          </div>
        </div>

        {/* Contact & Access */}
        <div className="space-y-6">
          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Contact</h2>
            <div className="mb-4">
              <label htmlFor="work_phone" className="mb-1 block font-medium">
                Work Phone
              </label>
              <input
                type="tel"
                id="work_phone"
                value={data.work_phone}
                onChange={(e) => setData("work_phone", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
            <div className="mb-4 grid gap-4 md:grid-cols-2">
              <div>
                <label htmlFor="cell_phone" className="mb-1 block font-medium">
                  Cell Phone
                </label>
                <input
                  type="tel"
                  id="cell_phone"
                  value={data.cell_phone}
                  onChange={(e) => setData("cell_phone", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                />
              </div>
              <div>
                <label htmlFor="home_phone" className="mb-1 block font-medium">
                  Home Phone
                </label>
                <input
                  type="tel"
                  id="home_phone"
                  value={data.home_phone}
                  onChange={(e) => setData("home_phone", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                />
              </div>
            </div>
          </div>

          {/* Signature (admin only) */}
          {isAdmin && (
            <div className="border-hpi-light-gray rounded border p-4">
              <h2 className="mb-4 text-lg font-semibold">Signature</h2>
              <div className="mb-4">
                <label htmlFor="sig_profile" className="mb-1 block font-medium">
                  Profile URL
                </label>
                <input
                  type="text"
                  id="sig_profile"
                  value={data.sig_profile}
                  onChange={(e) => setData("sig_profile", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  maxLength={100}
                />
              </div>
              <div className="mb-4">
                <label htmlFor="sig_vcard" className="mb-1 block font-medium">
                  vCard URL
                </label>
                <input
                  type="text"
                  id="sig_vcard"
                  value={data.sig_vcard}
                  onChange={(e) => setData("sig_vcard", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  maxLength={100}
                />
              </div>
              <label className="flex items-center gap-2">
                <input
                  type="checkbox"
                  checked={!!data.sig_show_cell}
                  onChange={(e) => setData("sig_show_cell", e.target.checked)}
                />
                <span className="text-sm">Show Cell Phone on Signature</span>
              </label>
            </div>
          )}

          {/* Password (only for new) */}
          {isNew && (
            <div className="border-hpi-light-gray rounded border p-4">
              <h2 className="mb-4 text-lg font-semibold">Password</h2>
              <div className="mb-4">
                <label htmlFor="password" className="mb-1 block font-medium">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  value={data.password ?? ""}
                  onChange={(e) => setData("password", e.target.value)}
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  minLength={12}
                  required={isNew}
                />
                {errors?.password && (
                  <p className="text-hpi-red mt-1 text-sm">
                    {errors.password.join(", ")}
                  </p>
                )}
              </div>
              <div className="mb-4">
                <label
                  htmlFor="password_confirmation"
                  className="mb-1 block font-medium"
                >
                  Confirm Password
                </label>
                <input
                  type="password"
                  id="password_confirmation"
                  value={data.password_confirmation ?? ""}
                  onChange={(e) =>
                    setData("password_confirmation", e.target.value)
                  }
                  className="border-hpi-light-gray w-full rounded border px-3 py-2"
                  minLength={12}
                  required={isNew}
                />
              </div>
            </div>
          )}

          {/* Roles */}
          <div className="border-hpi-light-gray rounded border p-4">
            <h2 className="mb-4 text-lg font-semibold">Roles</h2>
            <div className="flex flex-wrap gap-2">
              {role_options
                .filter((role) => isAdmin || role !== "admin")
                .map((role) => (
                  <label key={role} className="flex items-center gap-1.5">
                    <input
                      type="checkbox"
                      checked={data.roles.includes(role)}
                      onChange={() => toggleRole(role)}
                    />
                    <span className="text-sm">{role}</span>
                  </label>
                ))}
            </div>
          </div>
        </div>

        {/* Pay Info */}
        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Pay & Time Off</h2>
          <div className="mb-4 grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="org_level" className="mb-1 block font-medium">
                Access Level
              </label>
              <select
                id="org_level"
                value={data.org_level ?? 0}
                onChange={(e) => setData("org_level", parseInt(e.target.value))}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="0">None</option>
                <option value="5">Full Access</option>
                <option value="8">Limited Access</option>
              </select>
            </div>
            <div>
              <label
                htmlFor="employment_type"
                className="mb-1 block font-medium"
              >
                Employment Type
              </label>
              <select
                id="employment_type"
                value={data.employment_type ?? ""}
                onChange={(e) =>
                  setData(
                    "employment_type",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="">Select...</option>
                <option value="0">Full-Time</option>
                <option value="1">Part-Time</option>
                <option value="2">Contract</option>
              </select>
            </div>
            <div>
              <label htmlFor="worker_type" className="mb-1 block font-medium">
                Worker Type
              </label>
              <select
                id="worker_type"
                value={data.worker_type}
                onChange={(e) =>
                  setData("worker_type", parseInt(e.target.value))
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="0">Employee</option>
                <option value="1">Contractor</option>
              </select>
            </div>
          </div>
          <div className="mb-4 grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="pay_rate" className="mb-1 block font-medium">
                Pay Rate
              </label>
              <input
                type="number"
                id="pay_rate"
                value={data.pay_rate ?? ""}
                onChange={(e) =>
                  setData(
                    "pay_rate",
                    e.target.value ? parseFloat(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                step="0.01"
              />
            </div>
            <div>
              <label htmlFor="pay_type" className="mb-1 block font-medium">
                Pay Type
              </label>
              <select
                id="pay_type"
                value={data.pay_type}
                onChange={(e) => setData("pay_type", parseInt(e.target.value))}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              >
                <option value="0">Salary</option>
                <option value="1">Hourly</option>
                <option value="2">Flat Rate</option>
              </select>
            </div>
            <div>
              <label htmlFor="mileage_rate" className="mb-1 block font-medium">
                Mileage Rate
              </label>
              <input
                type="number"
                id="mileage_rate"
                value={data.mileage_rate ?? ""}
                onChange={(e) =>
                  setData(
                    "mileage_rate",
                    e.target.value ? parseFloat(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
                step="0.01"
              />
            </div>
          </div>
          <div className="mb-4 grid gap-4 md:grid-cols-3">
            <div>
              <label htmlFor="pto_days" className="mb-1 block font-medium">
                PTO Days
              </label>
              <input
                type="number"
                id="pto_days"
                value={data.pto_days ?? ""}
                onChange={(e) =>
                  setData(
                    "pto_days",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
            <div>
              <label htmlFor="rdo_days" className="mb-1 block font-medium">
                RDO Days
              </label>
              <input
                type="number"
                id="rdo_days"
                value={data.rdo_days ?? ""}
                onChange={(e) =>
                  setData(
                    "rdo_days",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
            <div>
              <label htmlFor="sick_days" className="mb-1 block font-medium">
                Sick Days
              </label>
              <input
                type="number"
                id="sick_days"
                value={data.sick_days ?? ""}
                onChange={(e) =>
                  setData(
                    "sick_days",
                    e.target.value ? parseInt(e.target.value) : null,
                  )
                }
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
          </div>
          <div className="mb-4 grid gap-4 md:grid-cols-2">
            <div>
              <label
                htmlFor="quickbooks_name"
                className="mb-1 block font-medium"
              >
                QuickBooks Name
              </label>
              <input
                type="text"
                id="quickbooks_name"
                value={data.quickbooks_name}
                onChange={(e) => setData("quickbooks_name", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
            <div>
              <label
                htmlFor="quickbooks_vendor"
                className="mb-1 block font-medium"
              >
                QuickBooks Vendor
              </label>
              <input
                type="text"
                id="quickbooks_vendor"
                value={data.quickbooks_vendor}
                onChange={(e) => setData("quickbooks_vendor", e.target.value)}
                className="border-hpi-light-gray w-full rounded border px-3 py-2"
              />
            </div>
          </div>
          <div className="mb-4">
            <label
              htmlFor="report_display_name"
              className="mb-1 block font-medium"
            >
              Name Displayed on Reports
            </label>
            <input
              type="text"
              id="report_display_name"
              value={data.report_display_name}
              onChange={(e) => setData("report_display_name", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
            />
          </div>
          <div className="mb-4">
            <label htmlFor="specialty" className="mb-1 block font-medium">
              Specialty / Notes
            </label>
            <textarea
              id="specialty"
              value={data.specialty}
              onChange={(e) => setData("specialty", e.target.value)}
              className="border-hpi-light-gray w-full rounded border px-3 py-2"
              rows={3}
            />
          </div>
        </div>

        {/* Permissions */}
        <div className="border-hpi-light-gray rounded border p-4">
          <h2 className="mb-4 text-lg font-semibold">Permissions</h2>
          {Object.entries(permission_categories).map(
            ([category, permissions]) => (
              <div key={category} className="mb-4">
                <h3 className="text-hpi-med-blue mb-2 text-sm font-semibold">
                  {category}
                </h3>
                <div className="grid grid-cols-2 gap-1">
                  {permissions.map((perm) => (
                    <label key={perm} className="flex items-center gap-1.5">
                      <input
                        type="checkbox"
                        checked={!!data.permissions[perm]}
                        onChange={() => togglePermission(perm)}
                      />
                      <span className="text-sm">{perm}</span>
                    </label>
                  ))}
                </div>
              </div>
            ),
          )}
        </div>
      </div>

      <div className="flex gap-2">
        <button
          type="submit"
          disabled={processing}
          className="bg-hpi-dark-blue hover:bg-hpi-blue rounded px-4 py-2 text-white disabled:opacity-50"
        >
          {submitLabel}
        </button>
        <Link
          href={employeesPath()}
          className="border-hpi-light-gray hover:bg-hpi-pale-blue rounded border px-4 py-2"
        >
          Cancel
        </Link>
      </div>
    </form>
  )
}
